import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import api, {
  API_ERRORS,
  getApiErrorMessage,
  getApiErrorName,
} from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { ArrowBackIcon } from '../../../assets/svg/SVGComponents';
import { categoryLabel } from '../../../utils/tournament/labels';
import PresentationForm from './PresentationForm';
import {
  EMPTY_SCORES,
  totalScore,
  TPresentationScores,
} from './presentationRules';
import {
  BackLink,
  Chips,
  Empty,
  Header,
  Layout,
  Page,
  Progress,
  Status,
  TeamItem,
  TeamList,
} from './styles';

interface IPresentationItem {
  team_id: string;
  team_name: string;
  school_name: string | null;
  category: string;
  disqualified: boolean;
  status: 'scored' | 'pending';
  scores: (TPresentationScores & { total_score: number }) | null;
}

const pickScores = (item: IPresentationItem): TPresentationScores =>
  item.scores
    ? {
        mechanical_score: item.scores.mechanical_score,
        programming_score: item.scores.programming_score,
        teamwork_score: item.scores.teamwork_score,
        communication_score: item.scores.communication_score,
      }
    : { ...EMPTY_SCORES };

/**
 * Banca da Apresentação Técnica (etapa nacional, categorias 3 e 4). Visível
 * para os juízes do torneio e o organizador. Os rascunhos ficam por equipe:
 * trocar de equipe no meio não apaga o que já foi lançado.
 */
const TournamentPresentations: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [tournamentName, setTournamentName] = useState('');
  const [items, setItems] = useState<IPresentationItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, TPresentationScores>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const leave = useCallback(
    (description: string) => {
      addToast({ type: 'error', expression: 'Ops!', description });
      navigate(`/tournament/${id}`, { replace: true });
    },
    [addToast, id, navigate],
  );

  useEffect(() => {
    let alive = true;

    Promise.all([
      api.get<{ name: string }>(`/tournaments/${id}`),
      api.get<IPresentationItem[]>(
        `/tournaments/${id}/technical-presentations`,
      ),
    ])
      .then(([{ data: tournament }, { data: list }]) => {
        if (!alive) return;
        setTournamentName(tournament.name);
        setItems(list);
        setDrafts(
          Object.fromEntries(
            list.map(item => [item.team_id, pickScores(item)]),
          ),
        );
        const firstPending = list.find(item => item.status === 'pending');
        setSelectedId((firstPending ?? list[0])?.team_id ?? null);
      })
      .catch(err => {
        if (!alive) return;
        leave(
          getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE
            ? 'Só os juízes cadastrados no torneio acessam a banca da apresentação técnica.'
            : (getApiErrorMessage(err) ??
                'Não foi possível carregar a banca da apresentação técnica.'),
        );
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [id, leave]);

  const categories = useMemo(
    () => Array.from(new Set(items.map(item => item.category))).sort(),
    [items],
  );

  const visible = useMemo(
    () => items.filter(item => !category || item.category === category),
    [items, category],
  );

  const scoredCount = items.filter(item => item.status === 'scored').length;
  const selected = items.find(item => item.team_id === selectedId) ?? null;

  const save = async () => {
    if (!selected) return;
    const scores = drafts[selected.team_id];
    setSaving(true);

    try {
      await api.post(
        `/tournaments/${id}/teams/${selected.team_id}/technical-presentation`,
        scores,
      );

      const updated = items.map(item =>
        item.team_id === selected.team_id
          ? {
              ...item,
              status: 'scored' as const,
              scores: { ...scores, total_score: totalScore(scores) },
            }
          : item,
      );
      setItems(updated);

      addToast({
        type: 'success',
        expression: 'Avaliação salva',
        description: `${selected.team_name}: ${totalScore(scores)} pontos.`,
      });

      // Segue para a próxima equipe pendente da mesma lista.
      const next = updated.find(
        item =>
          item.status === 'pending' &&
          (!category || item.category === category),
      );
      if (next) setSelectedId(next.team_id);
    } catch (err) {
      if (getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE) {
        leave(
          'Você não está cadastrado como juiz deste torneio. Peça à organização para incluir você.',
        );
        return;
      }
      addToast({
        type: 'error',
        expression: 'Não foi possível salvar',
        description: getApiErrorMessage(err) ?? 'Tente novamente em instantes.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <BackLink type="button" onClick={() => navigate(`/tournament/${id}`)}>
        <ArrowBackIcon
          aria-hidden
          width="1.33rem"
          stroke="#1E88E5"
          strokeWidth={3}
          fill="none"
        />
        {tournamentName || 'Torneio'}
      </BackLink>

      <Header>
        <div>
          <h1>Apresentação técnica</h1>
          <p>Banca da etapa nacional · categorias III e IV</p>
        </div>
        {!loading && items.length > 0 && (
          <Progress>
            {scoredCount} <span>de</span> {items.length} <span>avaliadas</span>
          </Progress>
        )}
      </Header>

      {loading && <Empty>Carregando equipes…</Empty>}

      {!loading && items.length === 0 && (
        <Empty>Nenhuma equipe com apresentação técnica neste torneio.</Empty>
      )}

      {!loading && items.length > 0 && (
        <Layout>
          <div>
            {categories.length > 1 && (
              <Chips role="group" aria-label="Filtrar por categoria">
                <button
                  type="button"
                  aria-pressed={category === null}
                  onClick={() => setCategory(null)}
                >
                  Todas
                </button>
                {categories.map(value => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={category === value}
                    onClick={() => setCategory(value)}
                  >
                    {categoryLabel(value)}
                  </button>
                ))}
              </Chips>
            )}

            <TeamList>
              {visible.map(item => (
                <TeamItem
                  key={item.team_id}
                  $active={item.team_id === selectedId}
                >
                  <button
                    type="button"
                    aria-current={item.team_id === selectedId}
                    onClick={() => setSelectedId(item.team_id)}
                  >
                    <strong>{item.team_name}</strong>
                    <Status $scored={item.status === 'scored'}>
                      {item.status === 'scored'
                        ? `${item.scores?.total_score ?? 0} pts`
                        : 'Pendente'}
                    </Status>
                    <small>
                      {[item.school_name, categoryLabel(item.category)]
                        .filter(Boolean)
                        .join(' · ')}
                      {item.disqualified ? ' · desclassificada' : ''}
                    </small>
                  </button>
                </TeamItem>
              ))}
            </TeamList>
          </div>

          {selected ? (
            <PresentationForm
              teamId={selected.team_id}
              teamName={selected.team_name}
              subtitle={[selected.school_name, categoryLabel(selected.category)]
                .filter(Boolean)
                .join(' · ')}
              scores={drafts[selected.team_id] ?? EMPTY_SCORES}
              alreadyScored={selected.status === 'scored'}
              saving={saving}
              onChange={scores =>
                setDrafts(prev => ({ ...prev, [selected.team_id]: scores }))
              }
              onSave={save}
            />
          ) : (
            <Empty>Escolha uma equipe na lista.</Empty>
          )}
        </Layout>
      )}
    </Page>
  );
};

export default TournamentPresentations;
